import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * VoiceInput - A reusable voice input component using Web Speech API
 * 
 * Props:
 * - onTranscript: (text: string) => void - Callback when speech is transcribed
 * - onListening: (isListening: boolean) => void - Callback for listening state
 * - placeholder: string - Placeholder text when not listening
 * - className: string - Additional CSS classes
 * - size: 'sm' | 'default' | 'lg' | 'icon' - Button size
 * - variant: 'default' | 'outline' | 'ghost' - Button variant
 * - continuous: boolean - Whether to keep listening after speech ends
 * - disabled: boolean - Disable the voice input
 */
export default function VoiceInput({
  onTranscript,
  onListening,
  placeholder = 'Click to speak',
  className = '',
  size = 'icon',
  variant = 'outline',
  continuous = false,
  disabled = false,
  showText = false,
  'data-testid': dataTestId = 'voice-input-btn'
}) {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const recognitionRef = useRef(null);

  useEffect(() => {
    // Check for Speech Recognition support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.log('Speech Recognition not supported in this browser');
      setIsSupported(false);
      return;
    }

    setIsSupported(true);

    try {
      // Initialize Speech Recognition
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = continuous;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
      recognitionRef.current.maxAlternatives = 1;

      recognitionRef.current.onstart = () => {
        console.log('Voice recognition started');
        setIsListening(true);
        onListening?.(true);
      };

      recognitionRef.current.onend = () => {
        console.log('Voice recognition ended');
        setIsListening(false);
        onListening?.(false);
        setInterimTranscript('');
      };

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        let interim = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interim += transcript;
          }
        }

        setInterimTranscript(interim);

        if (finalTranscript) {
          console.log('Final transcript:', finalTranscript);
          onTranscript?.(finalTranscript.trim());
          if (!continuous) {
            try {
              recognitionRef.current?.stop();
            } catch (e) {
              // Ignore stop errors
            }
          }
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        onListening?.(false);
        
        switch (event.error) {
          case 'not-allowed':
          case 'permission-denied':
            toast.error('Microphone access denied. Please allow microphone access in your browser settings.');
            break;
          case 'no-speech':
            // Silent - no speech detected, this is normal
            break;
          case 'audio-capture':
            toast.error('No microphone found. Please connect a microphone.');
            break;
          case 'network':
            toast.error('Network error. Please check your internet connection.');
            break;
          case 'aborted':
            // User or system aborted - no error message needed
            break;
          default:
            // Don't show error for common non-critical errors
            if (event.error !== 'service-not-allowed') {
              console.warn(`Voice input warning: ${event.error}`);
            }
        }
      };

      recognitionRef.current.onnomatch = () => {
        console.log('No speech match found');
      };

    } catch (error) {
      console.error('Failed to initialize speech recognition:', error);
      setIsSupported(false);
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [continuous, onTranscript, onListening]);

  const toggleListening = () => {
    if (!isSupported) {
      toast.error('Voice input is not supported in your browser. Please use Chrome or Edge.');
      return;
    }

    if (isListening) {
      try {
        recognitionRef.current?.stop();
      } catch (error) {
        console.error('Error stopping recognition:', error);
        setIsListening(false);
      }
    } else {
      try {
        // Ensure any previous session is stopped
        try {
          recognitionRef.current?.stop();
        } catch (e) {
          // Ignore
        }
        
        // Small delay before starting new session
        setTimeout(() => {
          try {
            recognitionRef.current?.start();
          } catch (error) {
            if (error.message?.includes('already started')) {
              // Already listening, ignore
            } else {
              console.error('Speech recognition start error:', error);
              toast.error('Could not start voice input. Please try again.');
            }
          }
        }, 100);
      } catch (error) {
        console.error('Speech recognition error:', error);
        toast.error('Could not start voice input. Please try again.');
      }
    }
  };

  if (!isSupported) {
    return null; // Don't render if not supported
  }

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <Button
        type="button"
        variant={isListening ? 'default' : variant}
        size={size}
        onClick={toggleListening}
        disabled={disabled}
        className={`${isListening ? 'bg-red-500 hover:bg-red-600 animate-pulse' : ''}`}
        title={isListening ? 'Stop listening' : placeholder}
        data-testid={dataTestId}
      >
        {isListening ? (
          <MicOff className="h-4 w-4" />
        ) : (
          <Mic className="h-4 w-4" />
        )}
        {showText && (
          <span className="ml-2">{isListening ? 'Stop' : 'Voice'}</span>
        )}
      </Button>
      
      {isListening && interimTranscript && (
        <span className="text-sm text-muted-foreground italic animate-pulse max-w-[200px] truncate">
          {interimTranscript}...
        </span>
      )}
    </div>
  );
}

/**
 * VoiceInputField - Voice input integrated with a text input field
 */
export function VoiceInputField({
  value,
  onChange,
  placeholder = 'Type or speak...',
  className = '',
  inputClassName = '',
  disabled = false,
  ...inputProps
}) {
  const [isListening, setIsListening] = useState(false);

  const handleTranscript = (text) => {
    // Append to existing value with space
    const newValue = value ? `${value} ${text}` : text;
    onChange({ target: { value: newValue } });
  };

  return (
    <div className={`relative flex items-center ${className}`}>
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pr-12 ${inputClassName}`}
        {...inputProps}
      />
      <div className="absolute right-2">
        <VoiceInput
          onTranscript={handleTranscript}
          onListening={setIsListening}
          placeholder="Click to speak"
          size="sm"
          variant="ghost"
          disabled={disabled}
        />
      </div>
    </div>
  );
}

/**
 * VoiceTextarea - Voice input integrated with a textarea
 */
export function VoiceTextarea({
  value,
  onChange,
  placeholder = 'Type or speak...',
  className = '',
  textareaClassName = '',
  disabled = false,
  rows = 3,
  ...textareaProps
}) {
  const [isListening, setIsListening] = useState(false);

  const handleTranscript = (text) => {
    const newValue = value ? `${value} ${text}` : text;
    onChange({ target: { value: newValue } });
  };

  return (
    <div className={`relative ${className}`}>
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        className={`flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pr-12 ${textareaClassName}`}
        {...textareaProps}
      />
      <div className="absolute right-2 top-2">
        <VoiceInput
          onTranscript={handleTranscript}
          onListening={setIsListening}
          placeholder="Click to speak"
          size="sm"
          variant="ghost"
          disabled={disabled}
        />
      </div>
    </div>
  );
}
